/**
 * Monitoreo.jsx
 * 
 * Página de monitoreo para supervisar repartidores en tiempo real
 * 
 * Qué hace:
 * - Permite generar links únicos para repartidores
 * - Muestra la posición del repartidor en tiempo real en un mapa
 * - Actualiza automáticamente la posición cada 2-3 segundos
 * - Muestra el estado del repartidor (activo/inactivo)
 * 
 * Funcionalidades:
 * - Generar link para compartir con el repartidor
 * - Ver posición en tiempo real
 * - Copiar link al portapapeles
 */

import React, { useState, useEffect, useRef } from 'react'
import { obtenerPosicionRepartidor, generarLinkRepartidor } from '../services/api'
import MapaMonitoreo from '../components/MapaMonitoreo'
import './Monitoreo.css'

function Monitoreo() {
  const [linkId, setLinkId] = useState(null)
  const [linkUrl, setLinkUrl] = useState('')
  const [posicionRepartidor, setPosicionRepartidor] = useState(null)
  const [estado, setEstado] = useState({ activo: false, ultima_actualizacion: null })
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [copiado, setCopiado] = useState(false)

  const intervaloRef = useRef(null)
  const intervaloActualRef = useRef(2500) // Intervalo actual (empieza en 2.5 segundos)
  const intentosSinConexionRef = useRef(0) // Contador de intentos sin conexión
  const tiempoInicioRef = useRef(null) // Tiempo cuando se inició el polling

  /**
   * Genera un nuevo link para el repartidor
   */
  const generarLink = async () => {
    setCargando(true)
    setError(null)

    try {
      const data = await generarLinkRepartidor()
      setLinkId(data.linkId)
      
      // Construir la URL completa del link
      const baseUrl = window.location.origin
      const urlCompleta = `${baseUrl}/repartidor/${data.linkId}`
      setLinkUrl(urlCompleta)
      
      // Iniciar el polling para obtener la posición
      iniciarPolling(data.linkId)
    } catch (err) {
      // Mensaje de error más descriptivo
      let mensajeError = err.message || 'Error al generar el link'
      
      // Mensajes específicos según el tipo de error
      if (mensajeError.includes('Failed to fetch') || mensajeError.includes('NetworkError')) {
        mensajeError = 'No se pudo conectar con el servidor. Verifica que:\n' +
          '1. El Worker esté desplegado en Cloudflare\n' +
          '2. La variable VITE_API_URL esté configurada en Cloudflare Pages\n' +
          '3. La URL del Worker sea correcta'
      } else if (mensajeError.includes('405')) {
        mensajeError = 'Método no permitido. El Worker puede no estar desplegado correctamente.'
      } else if (mensajeError.includes('Base de datos')) {
        mensajeError = 'Error con la base de datos. Verifica que D1 esté configurada y vinculada al Worker.'
      }
      
      setError(mensajeError)
      console.error('Error generando link:', err)
    } finally {
      setCargando(false)
    }
  }

  /**
   * Inicia el polling inteligente para obtener la posición del repartidor
   * OPTIMIZADO: Ajusta el intervalo según la actividad del repartidor
   */
  const iniciarPolling = (id) => {
    // Limpiar intervalo anterior si existe
    if (intervaloRef.current) {
      clearInterval(intervaloRef.current)
    }

    // Resetear contadores
    intervaloActualRef.current = 2500 // Empezar con 2.5 segundos
    intentosSinConexionRef.current = 0
    tiempoInicioRef.current = Date.now()

    // Obtener posición inmediatamente
    actualizarPosicion(id).then((data) => {
      ajustarIntervaloSegunActividad(data)
    })

    // Función recursiva para polling inteligente
    const hacerPolling = async () => {
      const data = await actualizarPosicion(id)
      ajustarIntervaloSegunActividad(data)
    }

    // Iniciar polling con intervalo dinámico
    intervaloRef.current = setInterval(hacerPolling, intervaloActualRef.current)
  }

  /**
   * Ajusta el intervalo de polling según la actividad del repartidor
   * - Activo: 2.5 segundos (rápido)
   * - Inactivo > 1 min: 10 segundos (lento)
   * - Nunca conectado > 5 min: 30 segundos (muy lento)
   * - Nunca conectado > 10 min: Detener polling
   */
  const ajustarIntervaloSegunActividad = (data) => {
    if (!data) {
      // No hay datos, incrementar contador
      intentosSinConexionRef.current++
      
      // Si han pasado más de 10 minutos sin conexión, detener polling
      const tiempoTranscurrido = Date.now() - tiempoInicioRef.current
      if (tiempoTranscurrido > 600000) { // 10 minutos
        console.log('⏹️ Deteniendo polling: Repartidor no se ha conectado en 10 minutos')
        if (intervaloRef.current) {
          clearInterval(intervaloRef.current)
          intervaloRef.current = null
        }
        return
      }
      
      // Si han pasado más de 5 minutos sin conexión, aumentar intervalo a 30 segundos
      if (tiempoTranscurrido > 300000) { // 5 minutos
        if (intervaloActualRef.current !== 30000) {
          intervaloActualRef.current = 30000
          reiniciarPollingConNuevoIntervalo()
          console.log('🐌 Polling lento: Repartidor no conectado, intervalo aumentado a 30 segundos')
        }
        return
      }
      
      // Si han pasado más de 1 minuto sin conexión, aumentar intervalo a 10 segundos
      if (tiempoTranscurrido > 60000) { // 1 minuto
        if (intervaloActualRef.current !== 10000) {
          intervaloActualRef.current = 10000
          reiniciarPollingConNuevoIntervalo()
          console.log('🐢 Polling moderado: Repartidor inactivo, intervalo aumentado a 10 segundos')
        }
      }
      return
    }

    // Hay datos, verificar si está activo
    const tiempoInactivo = data.tiempoInactivo || 0
    
    if (data.activo && tiempoInactivo < 30000) {
      // Repartidor activo, usar intervalo rápido (2.5 segundos)
      if (intervaloActualRef.current !== 2500) {
        intervaloActualRef.current = 2500
        reiniciarPollingConNuevoIntervalo()
        console.log('🚀 Polling rápido: Repartidor activo, intervalo reducido a 2.5 segundos')
      }
      intentosSinConexionRef.current = 0 // Resetear contador
    } else if (tiempoInactivo > 60000) {
      // Repartidor inactivo más de 1 minuto, usar intervalo lento (10 segundos)
      if (intervaloActualRef.current !== 10000) {
        intervaloActualRef.current = 10000
        reiniciarPollingConNuevoIntervalo()
        console.log('🐢 Polling moderado: Repartidor inactivo, intervalo aumentado a 10 segundos')
      }
    }
  }

  /**
   * Reinicia el polling con un nuevo intervalo
   */
  const reiniciarPollingConNuevoIntervalo = () => {
    if (intervaloRef.current) {
      clearInterval(intervaloRef.current)
    }
    
    if (linkId) {
      intervaloRef.current = setInterval(async () => {
        const data = await actualizarPosicion(linkId)
        ajustarIntervaloSegunActividad(data)
      }, intervaloActualRef.current)
    }
  }

  /**
   * Actualiza la posición del repartidor desde la API
   * OPTIMIZADO: Ahora usa un solo endpoint que devuelve posición Y estado (reduce peticiones a la mitad)
   */
  const actualizarPosicion = async (id) => {
    try {
      // Obtener posición Y estado en una sola petición (optimización)
      const data = await obtenerPosicionRepartidor(id)
      
      if (data && data.lat !== 0 && data.lng !== 0) {
        setPosicionRepartidor({
          lat: data.lat,
          lng: data.lng
        })
      }

      // El endpoint ya devuelve el estado combinado
      setEstado({
        activo: data.activo || false,
        ultima_actualizacion: data.ultima_actualizacion || null
      })

      return data // Retornar para que el polling inteligente pueda usarlo
    } catch (err) {
      // Si hay error, no mostrar en la UI (el repartidor puede no haberse conectado aún)
      console.log('Repartidor aún no se ha conectado o hay error:', err.message)
      return null
    }
  }

  /**
   * Copia el link al portapapeles
   */
  const copiarLink = async () => {
    if (!linkUrl) return

    try {
      await navigator.clipboard.writeText(linkUrl)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch (err) {
      console.error('Error copiando al portapapeles:', err)
      // Fallback para navegadores antiguos
      const textArea = document.createElement('textarea')
      textArea.value = linkUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    }
  }

  /**
   * Limpia el intervalo cuando el componente se desmonta
   */
  useEffect(() => {
    return () => {
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current)
      }
    }
  }, [])

  /**
   * Formatea la fecha de última actualización
   */
  const formatearFecha = (timestamp) => {
    if (!timestamp) return 'Nunca'
    
    const fecha = new Date(timestamp)
    const ahora = new Date()
    const diferencia = ahora - fecha

    if (diferencia < 60000) {
      return 'Hace menos de un minuto'
    } else if (diferencia < 3600000) {
      const minutos = Math.floor(diferencia / 60000)
      return `Hace ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`
    } else {
      return fecha.toLocaleTimeString('es-AR')
    }
  }

  return (
    <div className="monitoreo">
      <header className="monitoreo-header">
        <h1>TuRutaTrack - Monitoreo</h1>
        <p>Supervisa a tus repartidores en tiempo real</p>
      </header>

      <div className="monitoreo-contenido">
        <aside className="monitoreo-panel">
          <div className="panel-seccion">
            <h2>Gestión de Links</h2>
            
            {!linkId ? (
              <button 
                className="btn-generar-link"
                onClick={generarLink}
                disabled={cargando}
              >
                {cargando ? 'Generando...' : '📋 Generar Link para Repartidor'}
              </button>
            ) : (
              <div className="link-generado">
                <label>Link del Repartidor:</label>
                <div className="link-input-group">
                  <input
                    type="text"
                    value={linkUrl}
                    readOnly
                    className="link-input"
                  />
                  <button
                    className="btn-copiar"
                    onClick={copiarLink}
                    title="Copiar link"
                  >
                    {copiado ? '✓ Copiado' : '📋 Copiar'}
                  </button>
                </div>
                <p className="link-hint">
                  Comparte este link con el repartidor para comenzar el monitoreo
                </p>
                <button
                  className="btn-nuevo-link"
                  onClick={() => {
                    setLinkId(null)
                    setLinkUrl('')
                    setPosicionRepartidor(null)
                    setEstado({ activo: false, ultima_actualizacion: null })
                    if (intervaloRef.current) {
                      clearInterval(intervaloRef.current)
                      intervaloRef.current = null
                    }
                  }}
                >
                  🔄 Generar Nuevo Link
                </button>
              </div>
            )}

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}
          </div>

          {linkId && (
            <div className="panel-seccion">
              <h2>Estado del Repartidor</h2>
              
              <div className={`estado-badge ${estado.activo ? 'activo' : 'inactivo'}`}>
                {estado.activo ? (
                  <>
                    <span className="estado-indicador activo">●</span>
                    <span>Repartidor Activo</span>
                  </>
                ) : (
                  <>
                    <span className="estado-indicador inactivo">●</span>
                    <span>Repartidor Inactivo</span>
                  </>
                )}
              </div>

              {posicionRepartidor && (
                <div className="info-posicion">
                  <h3>Posición Actual</h3>
                  <div className="coordenadas">
                    <div className="coordenada-item">
                      <strong>Latitud:</strong>
                      <span>{posicionRepartidor.lat.toFixed(6)}</span>
                    </div>
                    <div className="coordenada-item">
                      <strong>Longitud:</strong>
                      <span>{posicionRepartidor.lng.toFixed(6)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="info-actualizacion">
                <strong>Última actualización:</strong>
                <span>{formatearFecha(estado.ultima_actualizacion)}</span>
              </div>

              {!estado.activo && (
                <div className="advertencia">
                  <p>⚠️ El repartidor aún no se ha conectado o está inactivo.</p>
                  <p>Comparte el link y pídele que abra la página en su dispositivo.</p>
                </div>
              )}
            </div>
          )}
        </aside>

        <main className="monitoreo-mapa">
          {linkId ? (
            <MapaMonitoreo
              posicionRepartidor={posicionRepartidor}
              activo={estado.activo}
            />
          ) : (
            <div className="mapa-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">🗺️</div>
                <h3>Genera un link para comenzar</h3>
                <p>Haz clic en "Generar Link para Repartidor" para crear un link único que compartirás con el repartidor.</p>
                <p>Una vez que el repartidor se conecte, verás su ubicación en tiempo real aquí.</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default Monitoreo

